import Plot from "react-plotly.js";

import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";

export type AffiliateAnalyticsSummary = {
  trackedLinks: number;
  clickedLinks?: number;
  convertedLinks?: number;
  clicks: number;
  conversions: number;
  approvedConversions: number;
  totalSaleAmount: number;
  totalPayoutAmount: number;
  eligiblePayoutAmount: number;
  topSourceTools?: { key: string; count: number }[];
};

export type AffiliateTimeseriesPoint = {
  date: string;
  links: number;
  clicks: number;
  conversions: number;
  approvedConversions: number;
  saleAmount: number;
  payoutAmount: number;
};

export type AffiliateTimeseriesResponse = {
  days: number;
  series: AffiliateTimeseriesPoint[];
};

const RANGE_OPTIONS = [30, 90, 180] as const;

const PLOT_CONFIG = { displayModeBar: false, responsive: true } as const;

const fmtMoney = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const fmtMoneyExact = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

const fmtNumber = (value: number): string =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value);

const ratePct = (numerator: number, denominator: number): number | null => {
  if (!denominator) return null;
  return (numerator / denominator) * 100;
};

const fmtPct = (value: number | null, digits = 1): string => {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(digits)}%`;
};

const ratio = (numerator: number, denominator: number): number | null => {
  if (!denominator) return null;
  return numerator / denominator;
};

const fmtRatio = (value: number | null, suffix: string): string => {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${fmtNumber(value)} ${suffix}`;
};

const sumField = (
  series: AffiliateTimeseriesPoint[],
  pick: (point: AffiliateTimeseriesPoint) => number,
  days?: number
): number => {
  const slice = days ? series.slice(-days) : series;
  return slice.reduce((total, point) => total + pick(point), 0);
};

const rollingPct = (
  series: AffiliateTimeseriesPoint[],
  window: number,
  numerator: (point: AffiliateTimeseriesPoint) => number,
  denominator: (point: AffiliateTimeseriesPoint) => number
): Array<number | null> => {
  return series.map((_, index) => {
    const start = Math.max(0, index - window + 1);
    const slice = series.slice(start, index + 1);
    return ratePct(
      slice.reduce((total, point) => total + numerator(point), 0),
      slice.reduce((total, point) => total + denominator(point), 0)
    );
  });
};

const toTitle = (value?: string): string => {
  if (!value) return "Unknown";
  return value
    .replace(/[-_]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

type FunnelStageProps = {
  label: string;
  value: number;
  widthPct: number;
  colorClass: string;
  caption?: string;
};

const FunnelStage = ({
  label,
  value,
  widthPct,
  colorClass,
  caption,
}: FunnelStageProps) => (
  <div>
    <div className="flex items-baseline justify-between gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="text-sm font-semibold text-gray-900">{fmtNumber(value)}</p>
    </div>
    <div className="mt-1 h-8 w-full rounded-md bg-gray-100 overflow-hidden">
      <div
        className={`h-full rounded-md ${colorClass} transition-all`}
        style={{ width: `${Math.max(widthPct, value > 0 ? 6 : 0)}%` }}
      />
    </div>
    {caption && <p className="mt-1 text-[11px] text-gray-500">{caption}</p>}
  </div>
);

type Props = {
  summary?: AffiliateAnalyticsSummary;
  timeseries?: AffiliateTimeseriesResponse;
  isLoading: boolean;
  rangeDays: number;
  onRangeDaysChange: (days: number) => void;
};

const AffiliateEarningsAnalytics = ({
  summary,
  timeseries,
  isLoading,
  rangeDays,
  onRangeDaysChange,
}: Props) => {
  const series = timeseries?.series || [];
  const trackedLinks = summary?.trackedLinks || 0;
  const clickedLinks = summary?.clickedLinks ?? 0;
  const convertedLinks = summary?.convertedLinks ?? 0;
  const clicks = summary?.clicks || 0;
  const conversions = summary?.conversions || 0;
  const sales = summary?.totalSaleAmount || 0;
  const payout = summary?.totalPayoutAmount || 0;

  const linkClickRate = ratePct(clickedLinks, trackedLinks);
  const clickToPurchaseRate = ratePct(conversions, clicks);
  const linkToPurchaseRate = ratePct(convertedLinks, trackedLinks);
  const clicksPerLink = ratio(clicks, trackedLinks);
  const payoutPerClick = ratio(payout, clicks);
  const payoutPerLink = ratio(payout, trackedLinks);
  const payoutPerPurchase = ratio(payout, conversions);
  const avgSale = ratio(sales, conversions);

  const last7Payout = sumField(series, (point) => point.payoutAmount, 7);
  const last30Payout = sumField(series, (point) => point.payoutAmount, 30);
  const last7Clicks = sumField(series, (point) => point.clicks, 7);
  const last30Clicks = sumField(series, (point) => point.clicks, 30);
  const last7Conv = sumField(series, (point) => point.conversions, 7);
  const last30Conv = sumField(series, (point) => point.conversions, 30);
  const last7Links = sumField(series, (point) => point.links, 7);
  const last30Links = sumField(series, (point) => point.links, 30);

  const recentPayoutDaily =
    last7Payout > 0 ? last7Payout / 7 : last30Payout / 30;
  const recentClickDaily =
    last7Clicks > 0 ? last7Clicks / 7 : last30Clicks / 30;
  const recentConvDaily = last7Conv > 0 ? last7Conv / 7 : last30Conv / 30;
  const recentLinkDaily = last7Links > 0 ? last7Links / 7 : last30Links / 30;
  const hasRecentActivity =
    last7Payout + last7Clicks + last7Conv + last30Payout + last30Clicks > 0;

  const projectedPayout30 = recentPayoutDaily * 30;
  const projectedClicks30 = recentClickDaily * 30;
  const projectedPurchases30 = recentConvDaily * 30;
  const projectedLinks30 = recentLinkDaily * 30;

  const sevenVsThirtyPayout = ratePct(last7Payout / 7, last30Payout / 30);

  const labels = series.map((point) => point.date);
  const rollingClickRate = rollingPct(
    series,
    7,
    (point) => point.clicks,
    (point) => point.links
  );
  const rollingPurchaseRate = rollingPct(
    series,
    7,
    (point) => point.conversions,
    (point) => point.clicks
  );

  const maxFunnel = Math.max(trackedLinks, clicks, conversions, 1);
  const sourceTools = summary?.topSourceTools || [];
  const hasVolume = series.some(
    (point) => point.links + point.clicks + point.conversions > 0
  );
  const hasEarnings = series.some(
    (point) => point.saleAmount + point.payoutAmount > 0
  );

  return (
    <Card className="sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Performance & projections
          </h3>
          <p className="text-sm text-gray-500">
            Funnel rates, recent run-rate, and a 30-day outlook from this
            customer&apos;s tracked links, clicks, and purchases.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          {RANGE_OPTIONS.map((days) => (
            <button
              key={days}
              type="button"
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                rangeDays === days
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => onRangeDaysChange(days)}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {isLoading && !summary ? (
        <div className="py-10 flex justify-center">
          <Spinner className="!w-[2rem]" />
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-5 rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-sm font-semibold text-gray-900">
                All-time conversion funnel
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Share of generated links that get clicked, then turn into a
                purchase.
              </p>
              <div className="space-y-3">
                <FunnelStage
                  label="Links generated"
                  value={trackedLinks}
                  widthPct={100}
                  colorClass="bg-indigo-500"
                />
                <FunnelStage
                  label="Links clicked"
                  value={clickedLinks}
                  widthPct={(clickedLinks / maxFunnel) * 100}
                  colorClass="bg-sky-500"
                  caption={`${fmtPct(
                    linkClickRate
                  )} of generated links received at least one click${
                    clicksPerLink !== null
                      ? ` · ${fmtRatio(clicksPerLink, "clicks/link")}`
                      : ""
                  }`}
                />
                <FunnelStage
                  label="Purchases"
                  value={conversions}
                  widthPct={(conversions / maxFunnel) * 100}
                  colorClass="bg-emerald-500"
                  caption={`${fmtPct(
                    clickToPurchaseRate
                  )} of clicks purchased · ${fmtPct(
                    linkToPurchaseRate
                  )} of links converted`}
                />
              </div>
            </div>

            <div className="lg:col-span-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <Card className="p-4 border border-indigo-100 bg-indigo-50/60">
                <p className="text-[11px] uppercase tracking-wide text-indigo-700">
                  Link → click
                </p>
                <p className="mt-1 text-2xl font-semibold text-indigo-900">
                  {fmtPct(linkClickRate)}
                </p>
                <p className="text-xs text-indigo-700 mt-1">
                  {fmtNumber(clickedLinks)} of {fmtNumber(trackedLinks)} links
                  clicked
                </p>
              </Card>
              <Card className="p-4 border border-sky-100 bg-sky-50/70">
                <p className="text-[11px] uppercase tracking-wide text-sky-700">
                  Click → purchase
                </p>
                <p className="mt-1 text-2xl font-semibold text-sky-900">
                  {fmtPct(clickToPurchaseRate)}
                </p>
                <p className="text-xs text-sky-700 mt-1">
                  {fmtNumber(conversions)} purchases from {fmtNumber(clicks)}{" "}
                  clicks
                </p>
              </Card>
              <Card className="p-4 border border-emerald-100 bg-emerald-50/70">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">
                  Payout / purchase
                </p>
                <p className="mt-1 text-2xl font-semibold text-emerald-900">
                  {fmtMoneyExact(payoutPerPurchase || 0)}
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  Avg sale {fmtMoneyExact(avgSale || 0)}
                </p>
              </Card>
              <Card className="p-4 border border-gray-200">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">
                  Payout / click
                </p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {fmtMoneyExact(payoutPerClick || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {fmtMoneyExact(payoutPerLink || 0)} per generated link
                </p>
              </Card>
              <Card className="p-4 border border-gray-200">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">
                  Approved share
                </p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {fmtPct(
                    ratePct(summary?.approvedConversions || 0, conversions)
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {fmtNumber(summary?.approvedConversions || 0)} approved of{" "}
                  {fmtNumber(conversions)}
                </p>
              </Card>
              <Card className="p-4 border border-gray-200">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">
                  Eligible payout
                </p>
                <p className="mt-1 text-xl font-semibold text-green-700">
                  {fmtMoneyExact(summary?.eligiblePayoutAmount || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Of {fmtMoneyExact(payout)} total attributed
                </p>
              </Card>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/70 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-sm font-semibold text-amber-950">
                Next 30 days (run-rate projection)
              </h4>
              <p className="text-[11px] text-amber-800">
                Uses last 7 days when there is activity, otherwise last 30 days.
                Not a guarantee — a planning estimate for this customer.
              </p>
            </div>
            {hasRecentActivity ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-md border border-amber-200 bg-white p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">
                    Projected payout
                  </p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">
                    {fmtMoney(projectedPayout30)}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {fmtMoneyExact(recentPayoutDaily)} / day recently
                  </p>
                </div>
                <div className="rounded-md border border-amber-200 bg-white p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">
                    Projected purchases
                  </p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">
                    {fmtNumber(projectedPurchases30)}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {fmtNumber(projectedClicks30)} clicks ·{" "}
                    {fmtNumber(projectedLinks30)} new links
                  </p>
                </div>
                <div className="rounded-md border border-amber-200 bg-white p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">
                    Last 7 vs 30-day pace
                  </p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">
                    {sevenVsThirtyPayout === null
                      ? "—"
                      : `${sevenVsThirtyPayout >= 100 ? "+" : ""}${(
                          sevenVsThirtyPayout - 100
                        ).toFixed(0)}%`}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {sevenVsThirtyPayout === null
                      ? "Need more payout history"
                      : sevenVsThirtyPayout >= 100
                      ? "Accelerating vs 30-day average"
                      : "Slowing vs 30-day average"}
                  </p>
                </div>
                <div className="rounded-md border border-amber-200 bg-white p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">
                    Recent window
                  </p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    7d {fmtMoneyExact(last7Payout)} · 30d{" "}
                    {fmtMoneyExact(last30Payout)}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {fmtNumber(last7Conv)} purchases in 7d ·{" "}
                    {fmtNumber(last30Conv)} in 30d
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-amber-900">
                Not enough recent clicks or purchases in the last {rangeDays}{" "}
                days to project a run-rate. Totals above still reflect lifetime
                activity.
              </p>
            )}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="px-1 text-sm font-semibold text-gray-900">
                Daily volume
              </p>
              <p className="px-1 text-xs text-gray-500 mb-1">
                New links, clicks, and purchases over the last {rangeDays} days.
              </p>
              {isLoading ? (
                <div className="py-12 flex justify-center">
                  <Spinner className="!w-[1.5rem]" />
                </div>
              ) : hasVolume ? (
                <Plot
                  data={[
                    {
                      type: "bar",
                      name: "Links",
                      x: labels,
                      y: series.map((point) => point.links),
                      marker: { color: "#6366f1" },
                      hovertemplate: "%{x}<br>Links: %{y}<extra></extra>",
                    },
                    {
                      type: "bar",
                      name: "Clicks",
                      x: labels,
                      y: series.map((point) => point.clicks),
                      marker: { color: "#0ea5e9" },
                      hovertemplate: "%{x}<br>Clicks: %{y}<extra></extra>",
                    },
                    {
                      type: "bar",
                      name: "Purchases",
                      x: labels,
                      y: series.map((point) => point.conversions),
                      marker: { color: "#10b981" },
                      hovertemplate: "%{x}<br>Purchases: %{y}<extra></extra>",
                    },
                  ]}
                  layout={{
                    barmode: "group",
                    height: 280,
                    margin: { t: 16, r: 16, b: 48, l: 40 },
                    paper_bgcolor: "transparent",
                    plot_bgcolor: "transparent",
                    hovermode: "x unified",
                    legend: { orientation: "h", y: 1.12 },
                    xaxis: { tickangle: -40, tickfont: { size: 10 } },
                    yaxis: { title: "Count", rangemode: "tozero" },
                  }}
                  config={PLOT_CONFIG}
                  style={{ width: "100%", height: "280px" }}
                />
              ) : (
                <p className="py-10 text-center text-sm text-gray-500">
                  No link, click, or purchase activity in this window.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="px-1 text-sm font-semibold text-gray-900">
                Rolling 7-day conversion rates
              </p>
              <p className="px-1 text-xs text-gray-500 mb-1">
                Clicks per new link, and purchases per click, smoothed over 7
                days.
              </p>
              {isLoading ? (
                <div className="py-12 flex justify-center">
                  <Spinner className="!w-[1.5rem]" />
                </div>
              ) : hasVolume ? (
                <Plot
                  data={[
                    {
                      type: "scatter",
                      mode: "lines",
                      name: "Clicks / link",
                      x: labels,
                      y: rollingClickRate,
                      line: { color: "#4f46e5", width: 2 },
                      hovertemplate:
                        "%{x}<br>Clicks per link: %{y:.1f}%<extra></extra>",
                    },
                    {
                      type: "scatter",
                      mode: "lines",
                      name: "Purchases / click",
                      x: labels,
                      y: rollingPurchaseRate,
                      line: { color: "#059669", width: 2 },
                      hovertemplate:
                        "%{x}<br>Purchase rate: %{y:.1f}%<extra></extra>",
                    },
                  ]}
                  layout={{
                    height: 280,
                    margin: { t: 16, r: 16, b: 48, l: 48 },
                    paper_bgcolor: "transparent",
                    plot_bgcolor: "transparent",
                    hovermode: "x unified",
                    legend: { orientation: "h", y: 1.12 },
                    xaxis: { tickangle: -40, tickfont: { size: 10 } },
                    yaxis: {
                      title: "Rate %",
                      rangemode: "tozero",
                      ticksuffix: "%",
                    },
                  }}
                  config={PLOT_CONFIG}
                  style={{ width: "100%", height: "280px" }}
                />
              ) : (
                <p className="py-10 text-center text-sm text-gray-500">
                  Rates appear once this customer has clicks in the selected
                  window.
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-5">
            <div className="lg:col-span-3 rounded-lg border border-gray-200 bg-white p-3">
              <p className="px-1 text-sm font-semibold text-gray-900">
                Sales & payout
              </p>
              <p className="px-1 text-xs text-gray-500 mb-1">
                Attributed cart value and affiliate payout by day.
              </p>
              {isLoading ? (
                <div className="py-12 flex justify-center">
                  <Spinner className="!w-[1.5rem]" />
                </div>
              ) : hasEarnings ? (
                <Plot
                  data={[
                    {
                      type: "scatter",
                      mode: "lines",
                      fill: "tozeroy",
                      name: "Sales",
                      x: labels,
                      y: series.map((point) => point.saleAmount),
                      line: { color: "#818cf8", width: 2 },
                      hovertemplate: "%{x}<br>Sales: $%{y:.2f}<extra></extra>",
                    },
                    {
                      type: "scatter",
                      mode: "lines",
                      fill: "tozeroy",
                      name: "Payout",
                      x: labels,
                      y: series.map((point) => point.payoutAmount),
                      line: { color: "#059669", width: 2 },
                      hovertemplate: "%{x}<br>Payout: $%{y:.2f}<extra></extra>",
                    },
                  ]}
                  layout={{
                    height: 280,
                    margin: { t: 16, r: 16, b: 48, l: 52 },
                    paper_bgcolor: "transparent",
                    plot_bgcolor: "transparent",
                    hovermode: "x unified",
                    legend: { orientation: "h", y: 1.12 },
                    xaxis: { tickangle: -40, tickfont: { size: 10 } },
                    yaxis: {
                      title: "USD",
                      rangemode: "tozero",
                      tickprefix: "$",
                    },
                  }}
                  config={PLOT_CONFIG}
                  style={{ width: "100%", height: "280px" }}
                />
              ) : (
                <p className="py-10 text-center text-sm text-gray-500">
                  No attributed sales in this window yet.
                </p>
              )}
            </div>

            <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-sm font-semibold text-gray-900">Source mix</p>
              <p className="text-xs text-gray-500 mb-3">
                Where this customer&apos;s tracked links are coming from.
              </p>
              {sourceTools.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">
                  No source-tool breakdown yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {sourceTools.map((item) => {
                    const width = ratePct(item.count, trackedLinks) || 0;
                    return (
                      <div key={item.key}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-xs font-medium text-gray-800">
                            {toTitle(item.key)}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {item.count} ·{" "}
                            {fmtPct(ratePct(item.count, trackedLinks))}
                          </span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-indigo-500"
                            style={{ width: `${Math.max(width, 3)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default AffiliateEarningsAnalytics;
